<template>
    <div v-if="FEATURES.contact_groups" class="col-span-4 lg:col-span-4">
        <analytics-sidebar-checkboxes v-model="senderTypesModel" :options="SENDER_TYPES">
            <template v-slot:title> Sender Type </template>
        </analytics-sidebar-checkboxes>
    </div>

    <div class="col-span-4 lg:col-span-4">
        <analytics-sidebar-box data-test="sender-box" :canReset="sender !== undefined" @reset="sender = undefined">
            <template v-slot:title> Sender </template>
            <vx-selectable
                size="sm"
                data-test="sender-filter"
                placeholder="All"
                label="name"
                :loading="loadingSenders"
                :clearable="false"
                @search="searchSenders"
                :options="sendersOptionsFiltered"
                v-model="sender"
            >
                <template v-slot:option="{ option }">
                    <font-awesome-icon
                        class="mr-2 text-slate-500 text-xs"
                        :icon="option.type === 'group' ? faGrid2Plus : faBuildingFlag"
                    ></font-awesome-icon>
                    {{ option.name }}
                </template>
                <template v-slot:selected-option="{ option }">
                    <div class="flex items-center">
                        <font-awesome-icon
                            class="mr-2 text-slate-500 text-xs"
                            :icon="option.type === 'group' ? faGrid2Plus : faBuildingFlag"
                        ></font-awesome-icon>
                        <span class="truncate">{{ option.name }}</span>
                    </div>
                </template>
            </vx-selectable>
        </analytics-sidebar-box>
    </div>
</template>

<script lang="ts" setup>
import { faBuildingFlag, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxSelectable } from '@voxie/frontend-components';
import { computed, inject, onMounted, ref, watch, type PropType } from 'vue';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { SENDER_TYPES, type SenderType } from '~/constants/senderTypes';
import type { Features } from '~/provides/features.types';
import { groupsClient, orgUnitsClient } from '~/services';
import type { Group } from '~/services/groups/groupsClient.types';
import type { OrgUnit } from '~/types/orgUnit.types';
import AnalyticsSidebarCheckboxes from '../partial/sidebar/AnalyticsSidebarCheckboxes.vue';
import type { Sender } from './AnalyticsBlastForecastSidebar.types';
import AnalyticsSidebarBox from '../partial/sidebar/AnalyticsSidebarBox.vue';
import { GROUP_STATUSES_CONFIGURABLE } from '~/constants/groupStatuses';

const FEATURES = inject('FEATURES') as Features;

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
});

const senderTypesModel = defineModel('senderTypes', {
    type: Array as PropType<SenderType[]>,
    required: false,
    default: () => [],
});

// model suffix necessary to not collide with the orgUnitId prop
const orgUnitIdModel = defineModel('orgUnitIdModel', {
    type: Number,
});

const groupIdModel = defineModel('groupId', {
    type: String,
});

const {
    loading: loadingSenders,
    response: sendersOptions,
    createSearch: createSendersSearch,
    cancelKey: sendersCancelKey,
} = useSearchRequest<Sender[]>(groupsClient);

const sendersOptionsFiltered = computed(() => {
    return (
        sendersOptions.value?.filter((sender) => {
            return senderTypesModel.value.includes(sender.type);
        }) || []
    );
});

const searchSenders = createSendersSearch(async (query: string) => {
    let options: Sender[] = [];

    if (!props.orgUnitId && senderTypesModel.value.includes('org_unit')) {
        const { data: orgUnitsResponse } = await orgUnitsClient.listOrgUnits(
            teamId,
            {
                query: query,
                setup_required: 0,
            },
            {
                cancelKey: sendersCancelKey,
            }
        );

        options = options.concat(
            orgUnitsResponse.data.map((orgUnit: OrgUnit) => ({
                id: orgUnit.id,
                name: orgUnit.name,
                type: 'org_unit',
            }))
        );
    }

    if (senderTypesModel.value.includes('group')) {
        const { data: groupsResponse } = await groupsClient.getGroups(
            { teamId, orgUnitId: props.orgUnitId },
            {
                query: query,
                setup_required: 0,
                ...(FEATURES.group_statuses ? { status: GROUP_STATUSES_CONFIGURABLE } : {}),
            },
            {
                cancelKey: sendersCancelKey,
            }
        );

        options = options.concat(
            groupsResponse.data.map((group: Group) => ({
                id: group.group_id,
                type: 'group',
                name: `${group.name} - ${group.group_id}`.trim(),
            }))
        );
    }

    return options;
});

onMounted(() => {
    searchSenders();
});

const sender = ref<Sender>();

// watch sender changes. necessary to watch the properties to avoid an infinite watch loop with the fetchParams.org_unit_id and fetchParams.group_id
watch([() => sender.value?.id, () => sender.value?.type], () => {
    if (sender.value) {
        if (sender.value.type === 'group') {
            groupIdModel.value = String(sender.value.id);
            orgUnitIdModel.value = undefined;
        } else {
            orgUnitIdModel.value = Number(sender.value.id);
            groupIdModel.value = undefined;
        }
    } else {
        groupIdModel.value = undefined;
        orgUnitIdModel.value = undefined;
    }
});

const shouldFetchSingleSender = () => {
    return !sendersOptions.value?.find((option) => {
        if (orgUnitIdModel.value && option.type == 'org_unit' && option.id == orgUnitIdModel.value) {
            return true;
        }

        if (groupIdModel.value && option.type == 'group' && option.id == groupIdModel.value) {
            return true;
        }

        return false;
    });
};

const fetchSingleSender = async () => {
    if (!shouldFetchSingleSender()) {
        return;
    }

    if (orgUnitIdModel.value) {
        try {
            const { data: orgUnit } = await orgUnitsClient.showOrgUnit(teamId, orgUnitIdModel.value);
            sender.value = {
                id: orgUnit.id,
                name: orgUnit.name,
                type: 'org_unit',
            };
        } catch (error) {
            console.error(error);
            sender.value = {
                id: orgUnitIdModel.value,
                name: String(orgUnitIdModel.value),
                type: 'org_unit',
            };
        }
    } else if (groupIdModel.value) {
        try {
            const { data: group } = await groupsClient.getGroup(
                { teamId, orgUnitId: props.orgUnitId },
                groupIdModel.value
            );
            sender.value = {
                id: group.group_id,
                name: `${group.name} - ${group.group_id}`.trim(),
                type: 'group',
            };
        } catch (error) {
            console.error(error);
            sender.value = {
                id: groupIdModel.value,
                name: String(groupIdModel.value),
                type: 'group',
            };
        }
    } else {
        sender.value = undefined;
    }
};

watch(
    [() => orgUnitIdModel.value, () => groupIdModel.value],
    async () => {
        fetchSingleSender();
    },
    {
        immediate: true,
    }
);

// clear sender whenever sender_types doesn't contain the type of the selected sender
watch(
    () => senderTypesModel.value,
    () => {
        if (!sender.value) {
            return;
        }

        if (senderTypesModel.value.includes('org_unit') && sender.value.type == 'org_unit') {
            return;
        }

        if (senderTypesModel.value.includes('group') && sender.value.type == 'group') {
            return;
        }

        sender.value = undefined;
    },
    {
        immediate: true,
    }
);
</script>
